import { AxiosError } from 'axios';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { notification } from 'ant-design-vue';
import {isLoginPage} from "@/utils/isLoginPage";
import { getRouterList, RouteItem } from '@/config/routerList';
import { isSilenced } from '@/services/session/sessionState';

function isBlockedByGate(e: any): boolean {
  return !!e && e.__spBlocked === true;
}

const MAX_BODY_CHARS = 300;

const BODY_MESSAGE_FIELDS = ['message', 'error_description', 'error', 'detail', 'description', 'title'];

/* Тело ответа не всегда строка: объект без разбора превратится в [object Object]. */
function stringifyResponseBody(body: any): string {
  if (typeof body === 'string') { return body; }
  if (typeof body === 'number' || typeof body === 'boolean') { return String(body); }
  if (!body || typeof body !== 'object') { return ''; }

  for (const field of BODY_MESSAGE_FIELDS) {
    const value = body[field];
    if (typeof value === 'string' && value.trim()) { return value.trim(); }
  }

  try {
    const json = JSON.stringify(body);
    if (!json || json === '{}' || json === '[]' || json === 'null') { return ''; }
    return json.length > MAX_BODY_CHARS ? json.slice(0, MAX_BODY_CHARS) + '…' : json;
  } catch (err) {
    return '';
  }
}

/* Пустое тело оставляем пустым: e.message — техническая строка axios, пользователю бесполезна. */
function responseErrorMessage(e: AxiosError): string {
  if (!e) { return ''; }
  if (!e.response) { return e.message; }

  return stringifyResponseBody(e.response.data);
}

interface NotificationData {
  id: string;
  title?: string;
  message: string;
  type: 'info' | 'warning' | 'confirmation';
  timestamp: Date;
  sourceUrl: string;
  moduleRoute: string;
  moduleName: string;
  userSelection?: string;
}

@Component
export default class Toast extends Vue {
  private notificationParams = {
    description: '',
    duration: 2,
    class: 'highest-stacking-order',
  }

  private readonly NOTIFICATIONS_STORAGE_KEY = 'sp_notifications';
  private readonly MAX_NOTIFICATIONS = 1000;

  private generateNotificationId(): string {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  private getModuleRoute(): string {
    return "/" + window.location.pathname.split('/')[1] || '';
  }

  private getModuleNameByRoute(routePath: string): string {
    // Use imported routerList to find the route
    const routerList = getRouterList();
    const route = this.findRouteByPath(routerList, routePath);
    return route ? route.title : '';
  }

  private findRouteByPath(routerList: RouteItem[], path: string): RouteItem | null {
    for (const route of routerList) {
      if (route.href === path) {
        return route;
      }
      if (route.child && Array.isArray(route.child)) {
        const found = this.findRouteByPath(route.child, path);
        if (found) return found;
      }
    }
    return null;
  }

  private buildNotification(message: string, type: 'info' | 'warning'): NotificationData {
    return {
      id: this.generateNotificationId(),
      message: message,
      type: type,
      timestamp: new Date(),
      sourceUrl: window.location.href,
      moduleRoute: this.getModuleRoute(),
      moduleName: this.getModuleNameByRoute(this.getModuleRoute()),
    };
  }

  private saveNotificationToStorage(notificationData: NotificationData): void {
    try {
      const existingNotifications = this.getNotificationsFromStorage();
      existingNotifications.unshift(notificationData);
      
      // Limit to MAX_NOTIFICATIONS
      if (existingNotifications.length > this.MAX_NOTIFICATIONS) {
        existingNotifications.splice(this.MAX_NOTIFICATIONS);
      }
      
      localStorage.setItem(this.NOTIFICATIONS_STORAGE_KEY, JSON.stringify(existingNotifications));
    } catch (error) {
      console.warn('Failed to save notification to localStorage:', error);
    }
  }

  private getNotificationsFromStorage(): NotificationData[] {
    try {
      const stored = localStorage.getItem(this.NOTIFICATIONS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to retrieve notifications from localStorage:', error);
      return [];
    }
  }

    showAction(
        message: string,
        config: any
    ) {
      if (isLoginPage() || isSilenced('info')) return;

      const [message1, message2] = message.split('.').map(s => s.trim());
      const key = `open${new Date()}`;
      const {action} = config;
      const [successAction] = action;
      
      const moduleRoute = this.getModuleRoute();
      const notificationData: NotificationData = {
        id: this.generateNotificationId(),
        title: message1 ? message1 : '',
        message: message2 ? message2 : '',
        type: 'confirmation',
        timestamp: new Date(),
        sourceUrl: window.location.href,
        moduleRoute: moduleRoute,
        moduleName: this.getModuleNameByRoute(this.getModuleRoute()),
        userSelection: undefined // Will be set when user clicks
      };

      notification.open({
        message: message2 || message1,
        description: message2 ? message1 : '',
        duration: 1000,
        btn: (h: any) => {
          return h(
              'a-button',
              {
                props: {
                  type: 'primary',
                  size: 'default',
                },
                on: {
                  click: () => {
                    notification.close(key);
                    notificationData.userSelection = successAction.text;
                    this.saveNotificationToStorage(notificationData);
                    successAction.onClick();
                  }
                },
              },
              successAction.text,
          );
        },
        key,
      })
    }

  showResponseError(e: AxiosError) {
    if (isLoginPage() || isBlockedByGate(e)) return;

    this.$log.warn(e.stack);
    this.showError(responseErrorMessage(e));
  }

  showInfo(message: string, duration?: number) {
    if (!message || isLoginPage()) return;

    if (isSilenced('info')) { return; }

    this.saveNotificationToStorage(this.buildNotification(message, 'info'));

    notification.success({
      message,
      ...this.notificationParams,
      duration: duration || this.notificationParams.duration,
    });
  }

  showError(rawMessage: any, duration?: number) {
    const message = typeof rawMessage === 'string' ? rawMessage : stringifyResponseBody(rawMessage);

    if (!message || isLoginPage()) return;

    if (isSilenced('error')) { return; }

    this.saveNotificationToStorage(this.buildNotification(message, 'warning'));

    notification.error({
      message,
      ...this.notificationParams,
      duration: duration || this.notificationParams.duration,
    });
  }

  // Public method to get all stored notifications
  getStoredNotifications(): NotificationData[] {
    return this.getNotificationsFromStorage();
  }

  // Public method to clear all stored notifications
  clearStoredNotifications(): void {
    localStorage.removeItem(this.NOTIFICATIONS_STORAGE_KEY);
  }
}
